import { useEffect, useState } from 'react';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import { PriceInput } from '../../../shared/components/PriceInput';
import { CustomOrderStatusBadge } from './CustomOrderStatusBadge';
import { AssetReview } from './AssetReview';
import { useCustomOrder, useMarkReady, useSetPricing } from '../hooks/useCustomOrders';
import type { CustomFieldSnapshot } from '../api/customOrdersApi';

const symbolFor = (currency: string) => (currency === 'NGN' ? '₦' : currency === 'USD' ? '$' : '');

const formatMoney = (amountMinor: number | null | undefined, currency: string) => {
  if (amountMinor == null) return '—';
  return `${symbolFor(currency)}${(amountMinor / 100).toLocaleString('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

/** Render an intake answer value (primitive / array / object) as readable text. */
function renderValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) return value.map((v) => renderValue(v)).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function labelFor(key: string, snapshot?: CustomFieldSnapshot[] | null): string {
  const def = snapshot?.find((d) => d.key === key);
  if (def?.label) return def.label;
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const sectionTitle = 'text-[12px] font-[700] uppercase tracking-wide text-muted-foreground mb-3';

interface Props {
  orderId: string;
  isOpen: boolean;
  onClose: () => void;
  canManage: boolean;
}

export const CustomOrderDetailModal = ({ orderId, isOpen, onClose, canManage }: Props) => {
  const { data: order, isLoading, error } = useCustomOrder(isOpen ? orderId : null);
  const setPricing = useSetPricing();
  const markReady = useMarkReady();

  const [priceMinor, setPriceMinor] = useState(0);

  useEffect(() => {
    if (order?.amountMinor != null) setPriceMinor(order.amountMinor);
  }, [order?.amountMinor]);

  const isDraft = order?.status === 'DRAFT' || order?.status === 'MEDIA_PROCESSING';
  const hasPrice = (order?.amountMinor ?? 0) > 0 || priceMinor > 0;
  const assets = order?.assets ?? [];
  const intakeEntries = order?.intake ? Object.entries(order.intake) : [];

  const handleSavePrice = () => {
    if (!order || priceMinor <= 0) return;
    setPricing.mutate({ id: order.id, amountMinor: priceMinor, currency: order.currency });
  };

  const handleMarkReady = () => {
    if (!order) return;
    markReady.mutate(order.id);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="p-8 max-h-[85vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-foreground">Custom order</h2>
              {order && <CustomOrderStatusBadge status={order.status} />}
            </div>
            {order && (
              <p className="text-[13px] text-muted-foreground mt-1">
                {order.customerEmail} · #{order.id.slice(0, 8)} · {formatDate(order.createdAt)}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-muted-foreground text-2xl leading-none">
            &times;
          </button>
        </div>

        {isLoading ? (
          <div className="py-16 text-center text-muted-foreground">Loading order…</div>
        ) : error || !order ? (
          <div className="py-16 text-center text-red-500">Failed to load this order. Please try again.</div>
        ) : (
          <div className="flex flex-col gap-8">
            {/* Intake */}
            <section>
              <h3 className={sectionTitle}>Request details</h3>
              {intakeEntries.length === 0 ? (
                <p className="text-[14px] text-muted-foreground">No intake details.</p>
              ) : (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                  {intakeEntries.map(([key, value]) => (
                    <div key={key}>
                      <dt className="text-[12px] text-muted-foreground">{labelFor(key, order.fieldSchemaSnapshot)}</dt>
                      <dd className="text-[14px] text-foreground">{renderValue(value)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </section>

            {/* Consultation */}
            {order.bookingId && (
              <section>
                <h3 className={sectionTitle}>Consultation</h3>
                <p className="text-[14px] text-foreground">
                  A consultation is linked to this order
                  <span className="text-muted-foreground"> (booking #{order.bookingId.slice(0, 8)})</span>.
                </p>
              </section>
            )}

            {/* Assets */}
            <section>
              <h3 className={sectionTitle}>Assets ({assets.length})</h3>
              {assets.length === 0 ? (
                <p className="text-[14px] text-muted-foreground">No uploaded assets.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {assets.map((asset) => (
                    <AssetReview key={asset.id} asset={asset} />
                  ))}
                </div>
              )}
            </section>

            {/* Pricing + Mark ready */}
            <section>
              <h3 className={sectionTitle}>Pricing</h3>
              {isDraft && canManage ? (
                <div className="flex flex-wrap items-end gap-3">
                  <div className="w-48">
                    <PriceInput label={`Price (${order.currency})`} value={priceMinor} onChange={setPriceMinor} />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSavePrice}
                    disabled={priceMinor <= 0 || setPricing.isPending}
                    loading={setPricing.isPending}
                  >
                    Save price
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleMarkReady}
                    disabled={!hasPrice || markReady.isPending}
                    loading={markReady.isPending}
                  >
                    Mark as ready
                  </Button>
                  {!hasPrice && (
                    <span className="text-[12px] text-muted-foreground self-center">Set a price to enable “Mark as ready”.</span>
                  )}
                </div>
              ) : (
                <p className="text-[14px] text-foreground">{formatMoney(order.amountMinor, order.currency)}</p>
              )}
            </section>

            {/* History */}
            {order.history && order.history.length > 0 && (
              <section>
                <h3 className={sectionTitle}>History</h3>
                <ol className="flex flex-col gap-2">
                  {order.history.map((h) => (
                    <li key={h.id} className="flex items-center gap-3 text-[13px]">
                      <CustomOrderStatusBadge status={h.status} />
                      <span className="text-muted-foreground">{formatDate(h.createdAt)}</span>
                      {h.note && <span className="text-foreground">— {h.note}</span>}
                    </li>
                  ))}
                </ol>
              </section>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};
